import React, { useState } from 'react';
import { FormInput } from '../../common/FormInput';
import { Button } from '../../common/Button';
import coachProfileService from '../../../services/coachProfileService';

export const PasswordResetModal = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState('');

  const handleEmailChange = (e) => setEmail(e.target.value);

  // Send OTP to email
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await coachProfileService.forgotPassword(email);
      setLoading(false);
      setStep(2);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Failed to send code. Please try again.');
    }
  };

  // Verify OTP and get reset token
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await coachProfileService.verifyOtp(email, code);
      setResetToken(response.resetToken);
      setLoading(false);
      setStep(1); // Reset step for next time
      setEmail('');
      setCode('');
      // Call onComplete with resetToken to open PasswordSetModal
      onComplete && onComplete(response.resetToken);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Invalid code. Please try again.');
    }
  };

  const handleClose = () => {
    setStep(1); // Reset step for next time
    setEmail('');
    setCode('');
    setError('');
    setResetToken('');
    onClose && onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-50">
      <div className="bg-jp-card border border-jp-outline/70 rounded-2xl p-8 shadow-xl w-full max-w-xl mx-auto relative">
        {/* Close button */}
        <button
          className="absolute top-6 right-6 text-jp-muted hover:text-jp-ink text-2xl"
          onClick={handleClose}
          aria-label="Close"
        >
          &times;
        </button>
        <h2 className="text-jp-ink text-2xl font-bold mb-10 text-center">PASSWORD RESET</h2>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm text-center">
            {error}
          </div>
        )}

        {step === 1 && (
          <form className="flex flex-col gap-8" onSubmit={handleEmailSubmit}>
            <FormInput
              label="Work email"
              name="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              required
              placeholder="Enter your email"
            />
            <Button type="submit" loading={loading} fullWidth={false} className="mt-4 mx-auto">
              {loading ? "Sending..." : "SEND CODE"}
            </Button>
          </form>
        )}
        {step === 2 && (
          <form className="flex flex-col gap-8" onSubmit={handleCodeSubmit}>
            <div>
              <label className="block text-jp-ink text-lg mb-2">
                Code <span className="text-base text-jp-ink">
                  (check your email to confirm the password reset)
                </span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  required
                  value={code}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(val);
                  }}
                  maxLength={6}
                  className="w-full rounded-xl border border-jp-line bg-jp-field text-jp-ink py-3 px-4 outline-none focus:ring-2 focus:ring-jp-accent/40 transition pr-12 text-center text-xl tracking-[0.3em] font-mono"
                  placeholder="Enter 6-digit code"
                />
                {/* Success check icon - only show when all 6 digits entered */}
                {code.length === 6 && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-900 bg-cyan-500 rounded-full w-8 h-8 flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                )}
              </div>
            </div>
            <Button type="submit" loading={loading} fullWidth={false} className="mt-4 mx-auto">
              {loading ? "Verifying..." : "NEXT"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
